import {
  Component,
  effect,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AiStore } from '../wh-ai.store';
import { TranslateModule } from '@ngx-translate/core';
import { WHButtonComponent } from 'src/app/app-shared/components/buttons/w-h-button/w-h-button.component';

@Component({
  selector: 'app-wh-ai-session-history',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    WHButtonComponent,
  ],
  template: `
    @let noData =
      aiStore.pastSessions()?.content.length === 0 &&
      !aiStore.isLoadingSessions();

    <div class="header p-b--8 flex--r-c flex-1 gap--8">
      @if (!noData) {
        <mat-icon>history</mat-icon>
        <span class="w-h-label" data-cy="wh-chat-label">
          {{ 'app.ai.chats.title' | translate }}
        </span>
      }
    </div>
    <div
      class="session-history flex--c-fs"
      [ngClass]="{ 'no-data': noData }"
      #scrollContainer
      (scroll)="onScroll()"
    >
      @if (noData) {
        <div class="no-sessions flex--col gap--16">
          <div class="flex--c-c-c gap--8">
            <mat-icon>hourglass_empty</mat-icon>
            <span>{{ 'app.ai.noSessionsAvailable.label' | translate }}</span>
          </div>
          <w-h-button
            [text]="'app.ai.askQuestion.button.label' | translate"
            type="stroked"
            (clicked)="aiStore.toggleIsSessionHistoryActive()"
          />
        </div>
      } @else {
        @for (
          session of aiStore.pastSessions()?.content;
          track session.sessionId
        ) {
          <button
            mat-button
            data-cy="wh-ai-session-item"
            [class.active]="session.sessionId === aiStore.activeSessionId()"
            (click)="handleSessionClick(session.sessionId)"
          >
            {{
              session.title ?? ('app.ai.newSession.button.label' | translate)
            }}
          </button>
        }
      }
      @if (aiStore.isLoadingSessions()) {
        <div class="loading-indicator flex--c-c">
          <mat-spinner diameter="24"></mat-spinner>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .header {
        .w-h-label {
          margin-bottom: 0;
        }
      }
      .session-history {
        overflow-y: auto;
        max-height: calc(100vh - 140px);

        &.no-data {
          justify-content: center;
          align-items: center;
          height: 100%;
        }
      }

      .session-history button {
        flex-shrink: 0;
      }
      .loading-indicator {
        padding: 8px;
      }
      button.active {
        border: 1px solid var(--mat-button-outlined-outline-color) !important;
        border-radius: 4px;
      }
    `,
  ],
})
export class WHAiSessionHistoryComponent {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLElement>;
  aiStore = inject(AiStore);

  constructor() {
    effect(() => {
      if (this.aiStore.isAiActive() && this.aiStore.isSessionHistoryActive()) {
        this.aiStore.loadUserSessions({
          page: 0,
          size: this.aiStore.params().size,
          reset: true,
        });
      }
    });
  }

  handleSessionClick(sessionId: string): void {
    sessionStorage.setItem(
      'wh-ai-session',
      JSON.stringify({ sessionId: sessionId, timestamp: Date.now() }),
    );
    this.aiStore.loadChatHistory(sessionId);
    this.aiStore.toggleIsSessionHistoryActive();
  }

  onScroll(): void {
    const element = this.scrollContainer.nativeElement;
    const threshold = 50;
    const isNearBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight <
      threshold;

    if (isNearBottom) {
      this.aiStore.loadMoreSessions();
    }
  }
}
